import { useEffect, useState } from 'react';
import type { ServerView } from '@hearth/shared';
import type { Api } from '../api';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { confirmText } from './serverWords';

// What the servers list and a server's page share: how they refresh, and Wake and Sleep.

/**
 * Calls `load` now, then every `everyMs` while the tab is visible, and at once when it's shown
 * again; nothing while it's hidden.
 */
export function useRefresh(load: () => Promise<void>, everyMs: number): void {
  useEffect(() => void load(), [load]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') void load();
    };
    const timer = setInterval(refresh, everyMs);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load, everyMs]);
}

/**
 * Wake and Sleep, asked first: `askWake` / `askSleep` open the question (render `dialog`); once
 * confirmed, the request goes out, then `after` reloads. A refusal goes to `failed`, as thrown
 * (a page tells "not found" from the rest).
 */
export function useWakeSleep(api: Api, after: () => Promise<void>, failed: (err: unknown) => void) {
  const [ask, setAsk] = useState<{ server: ServerView; action: 'wake' | 'sleep' }>();
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (!ask) return;
    setBusy(true);
    try {
      await api.post(`/v1/servers/${encodeURIComponent(ask.server.serverId)}/${ask.action === 'wake' ? 'start' : 'stop'}`);
      await after();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
      setAsk(undefined);
    }
  }

  const question = ask && confirmText(ask.server, ask.action);
  const dialog = (
    <ConfirmDialog
      open={!!ask}
      title={question?.title ?? ''}
      confirmLabel={ask?.action === 'wake' ? 'Wake' : 'Sleep'}
      tone={ask?.action === 'wake' ? 'ember' : 'moon'}
      busy={busy}
      onConfirm={() => void confirm()}
      onCancel={() => setAsk(undefined)}
    >
      <p>{question?.body}</p>
    </ConfirmDialog>
  );
  return {
    askWake: (server: ServerView) => setAsk({ server, action: 'wake' }),
    askSleep: (server: ServerView) => setAsk({ server, action: 'sleep' }),
    dialog,
  };
}
